import React, { Fragment } from 'react';
import classNameBind from 'classnames/bind';
import Icon from '../../../Icon';
import Popover from '../../../Popover';
import styles from './TreeSelectionListInner.module.css';
import { TextField } from '../../../TextField';
import Spinner from '../../../Spinner';
import Tooltip from '../../../Tooltip';

const bindClasses = classNameBind.bind(styles);

const TreeSelectionListInner = ({
  items,
  onItemSelected,
  value,
  globalSearch,
  dataId,
  hideSelected,
  grouped,
  searchValue,
  onItemSearch,
  isLoading,
  close,
  renderAfterListElement,
  t,
} = {}) => {
  const isItemChecked = item => (
    value.some((selectedItem) => {
      if (selectedItem.id) {
        return selectedItem.id === (item.id || item.value);
      }

      return selectedItem.name === (item.name || item.value);
    }));

  const isItemInGroupChecked = itemGroup => itemGroup.some(
    item => (
      item.subItems && item.subItems.length > 0 ? isItemInGroupChecked(item.subItems) : isItemChecked(item)
    ),
  );

  const refs = {};

  const openedItem = (item, lvl) => {
    const id = item.id ? item.id : item.name;
    Object.keys(refs[lvl]).forEach((prop) => {
      if (prop !== id && refs[lvl][prop].current) {
        refs[lvl][prop].current.onToggle(false);
      }
    });
  };

  const generateItemInner = (item, checked, submenu, lvl) => (
    <li
      key={item.id ? item.id : item.name}
      className={bindClasses('item', { checked, disabled: item.disabled })}
      onMouseEnter={() => { openedItem(item, lvl); }}
      data-ppuc-id={`${dataId}-item`}
    >
      <Tooltip content={item.tooltip || null}>
        <div className={bindClasses('item-ctn')}>
          <div className={bindClasses('item-text')}>
            <a
              onClick={(!submenu && !item.disabled) ? () => { onItemSelected(item); } : () => {}}
              role="button"
              aria-hidden
            >
              {checked && !hideSelected && <Icon name="checkmark" color="blue" />}
              <span className={bindClasses({ selected: checked, 'not-selected': !checked, 'submenu-trigger': submenu, 'hide-selected': hideSelected })}>{item.name}</span>
              {submenu ? (<span className={bindClasses('menu-icon-ctn')}><Icon name="chevron-right" color="default" /></span>) : ''}
            </a>
          </div>
          {item.rightButton && !submenu && (
            <div className={bindClasses('item-button')}>
              {item.rightButton}
            </div>
          )}
        </div>
        {item.breadCrumbs && item.breadCrumbs.length > 0 && !submenu && (
          <div
            className={bindClasses('item-caption', { 'hide-selected': hideSelected })}
            onClick={!submenu ? () => { onItemSelected(item); } : () => {}}
            role="button"
            aria-hidden
            data-ppuc-id={`${dataId}-item-breadcrumbs`}
          >
            <div className={bindClasses('item-breadcrumb')}>
              {item.breadCrumbs.join(' → ')}
            </div>
          </div>
        )}
      </Tooltip>
    </li>
  );

  const generateItem = (item, lvl) => {
    refs[lvl] = refs[lvl] || {};
    refs[lvl][item.id ? item.id : item.name] = React.createRef();
    if (item.subItems && item.subItems.length > 0) {
      const checked = isItemInGroupChecked(item.subItems);

      return (
        <Popover
          hoverEnable
          position="right-start"
          submenu
          noPadding
          ref={refs[lvl][item.id ? item.id : item.name]}
          key={item.id ? item.id : item.name}
          openClass={bindClasses('active')}
          activatorElement={generateItemInner(item, checked, true, lvl)}
          dataId={`TreeSelectionList_${item.id ? item.id : item.name}`}
          disableFocusStyle
          disabled={item.disabled}
        >
          <ul className={bindClasses('items')} key={item.id ? item.id : item.name}>
            {item.subItems.map(i => (generateItem(i, lvl + 1)))}
          </ul>
        </Popover>
      );
    }
    const checked = isItemChecked(item);

    return generateItemInner(item, checked, false, lvl);
  };

  return (
    <>
      {globalSearch && (
      <Popover.Section padding="narrow">
        <TextField
          name="search"
          icon="search"
          value={searchValue}
          onChange={onItemSearch}
          clearable
          autoFocus
        />
      </Popover.Section>
      )}
      <Popover.Section>
        <div className={bindClasses('wrapper')} data-ppuc-id={`${dataId}-wrapper`}>
          {isLoading && (
            <div className={bindClasses('spinner-ctn')}><Spinner size="medium" dataId={`${dataId}-loading`} /></div>
          )}
          {items.length === 0 && !isLoading && (
            <div className={bindClasses('noItems')}>
              {t('actionlist.no-items-available')}
            </div>
          )}
          {!isLoading && grouped && items.map(item => (
            <div
              key={`${dataId}_${item.id}_group`}
              className={bindClasses('group')}
              data-ppuc-id={`${dataId}-group`}
            >
              <ul className={bindClasses('items')}>
                {item.name && (
                <li className={bindClasses('group-label')} data-ppuc-id={`${dataId}.groupLabel`}>
                  <a>
                    <span className={bindClasses('not-selected', { 'hide-selected': hideSelected })}>
                      {item.name}
                    </span>
                  </a>
                </li>
                )}
                {item.subItems.map(subItem => (generateItem(subItem, 0)))}
              </ul>
            </div>
          ))}
          {!grouped && !isLoading && (
          <div
            className={bindClasses('group')}
            data-ppuc-id={`${dataId}-group`}
          >
            <ul className={bindClasses('items')}>
              {items.map(item => (generateItem(item, 0)))}
            </ul>
          </div>
          )}
        </div>
      </Popover.Section>
      {renderAfterListElement && renderAfterListElement(close)}
    </>
  );
};

export default TreeSelectionListInner;
